import { useEffect, useRef } from 'react';
import type { RankedBeach } from '../lib/forecast';
import type { ForecastData } from '../lib/openMeteo';
import { compass, scoreColor, verdict } from '../lib/scoring';
import Sparkline from './Sparkline';

const n1 = (v: number | null, digits = 2) => (v == null ? '–' : v.toFixed(digits));
const nRound = (v: number | null) => (v == null ? '–' : Math.round(v));

interface Props {
  ranked: RankedBeach[];
  data: ForecastData;
  selIdx: number;
  nowIdx: number;
  selectedId: string | null;
  /** true when the selection came from the map, so the list should scroll to it */
  revealId: string | null;
  onSelectBeach: (beachId: string) => void;
}

export default function BeachList({
  ranked,
  data,
  selIdx,
  nowIdx,
  selectedId,
  revealId,
  onSelectBeach,
}: Props) {
  const cardsRef = useRef<Record<string, HTMLButtonElement | null>>({});

  useEffect(() => {
    if (!revealId) return;
    cardsRef.current[revealId]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [revealId]);

  return (
    <ol id="beachlist">
      {ranked.map(({ beach, index, conditions: d }, pos) => {
        const [vTxt, vCol] = verdict(d.score);
        const selected = beach.id === selectedId;
        return (
          <li key={beach.id}>
            <button
              ref={(el) => {
                cardsRef.current[beach.id] = el;
              }}
              type="button"
              className={`card${pos === 0 ? ' top' : ''}${selected ? ' selected' : ''}`}
              aria-pressed={selected}
              onClick={() => onSelectBeach(beach.id)}
            >
              <span className="row1">
                <span className="scoreflag" style={{ background: scoreColor(d.score) }}>
                  <span className="n">{d.score == null ? '–' : d.score.toFixed(1)}</span>
                  <span className="u">/10</span>
                </span>
                <span className="names">
                  <span className="bname">
                    {pos === 0 && (
                      <span className="pill" aria-label="best right now">
                        ⚑ BEST
                      </span>
                    )}
                    {beach.name}
                  </span>
                  <span className="bmeta">
                    {beach.area} · {n1(d.effH)} m eff ({n1(d.rawH)} m {compass(d.dir)}) · T{' '}
                    {n1(d.per, 0)} s
                  </span>
                  <span className="bmeta">
                    wind {nRound(d.wind)} km/h {compass(d.wdir)} · gusts {nRound(d.gust)} ·{' '}
                    {nRound(d.temp)}°C
                  </span>
                </span>
                <span className="verdict" style={{ color: vCol }}>
                  {vTxt}
                </span>
              </span>
              <Sparkline data={data} beachIndex={index} selIdx={selIdx} nowIdx={nowIdx} />
            </button>
          </li>
        );
      })}
    </ol>
  );
}
